import { useEffect, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { IconButton } from "@/shared/ui/core/IconButton";
import { LoadingOverlay } from "@/shared/ui/core/Loading";

import {
  type ModalHeaderTone,
  type ModalIconTone,
  type ModalIconVariant,
  modalHeaderToneClass,
  modalIconBadgeToneClass,
} from "@/shared/ui/core/Modal";

export type DrawerPlacement = "left" | "right" | "bottom";

/** Width token for `placement="right"` (ignored for left/bottom). */
export type DrawerSize = "sm" | "md" | "lg";

const RIGHT_SIZE_CLASS: Record<DrawerSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-xl",
};

export type DrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Optional icon in front of title (kích thước = icon X: size-4). */
  icon?: ReactNode;
  /** Presentation style: 'badge' (soft-tinted rounded box matching close X button) or 'inline' (naked glyph). Default 'badge'. */
  iconVariant?: ModalIconVariant;
  /** Color theme for icon badge/glyph. Default 'primary'. */
  iconTone?: ModalIconTone;
  /** Optional custom class for title icon. */
  iconClassName?: string;
  /** Header tone theme: 'create' for soft green, 'edit' for soft orange/amber, or 'neutral'/'default'. */
  headerTone?: ModalHeaderTone;
  /** Optional custom classes for drawer header bar. */
  headerClassName?: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  placement?: DrawerPlacement;
  /** Right drawer width. Default `md` (max-w-md). */
  size?: DrawerSize;
  dismissible?: boolean;
  /** Block overlay click (form sheets with Select). Default true. */
  preventOutsideClose?: boolean;
  /** MOB create sheet: cover the viewport including bottom nav. */
  fullHeight?: boolean;
  className?: string;
  /** Override scroll body padding (e.g. nav drawer). */
  bodyClassName?: string;
  loading?: boolean;
  loadingLabel?: ReactNode;
};

export function Drawer({
  open,
  onOpenChange,
  title,
  icon,
  iconVariant = "badge",
  iconTone = "primary",
  iconClassName,
  headerTone = "default",
  headerClassName,
  description,
  children,
  footer,
  placement = "right",
  size = "md",
  dismissible = true,
  preventOutsideClose = true,
  fullHeight = false,
  className,
  bodyClassName,
  loading = false,
  loadingLabel = "Đang lưu...",
}: DrawerProps) {
  const isBottom = placement === "bottom";
  const isLeft = placement === "left";
  const blockOutside = !dismissible || preventOutsideClose;

  // Lock background scroll on both html and body when drawer is open
  useEffect(() => {
    if (!open) return;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-[var(--z-drawer)] bg-[var(--color-overlay)] touch-none overscroll-none"
          onClick={blockOutside ? (e) => e.preventDefault() : undefined}
        />
        <Dialog.Content
          className={cn(
            "fixed z-[calc(var(--z-drawer)+1)] flex flex-col bg-surface shadow-3 focus:outline-none overflow-hidden",
            (headerTone !== 'default' && headerTone !== 'neutral')
              ? "border-black/15 dark:border-white/10"
              : "border-border",
            isBottom && fullHeight
              ? "inset-x-0 bottom-0 h-[100dvh] max-h-[100dvh] rounded-none border-t pb-[env(safe-area-inset-bottom)]"
              : isBottom
                ? "inset-x-0 bottom-0 max-h-[min(85dvh,40rem)] rounded-t-modal border-t pb-[env(safe-area-inset-bottom)]"
                : isLeft
                  ? "inset-y-0 left-0 h-full w-full max-w-[min(20rem,88vw)] border-r"
                  : cn(
                    "inset-y-0 right-0 h-full w-full border-l",
                    RIGHT_SIZE_CLASS[size],
                  ),
            className,
            "overscroll-contain",
          )}
          onEscapeKeyDown={dismissible ? undefined : (e) => e.preventDefault()}
          onInteractOutside={
            blockOutside ? (e) => e.preventDefault() : undefined
          }
        >
          {(() => {
            const isDarkHeader = headerTone !== 'default' && headerTone !== 'neutral';
            return (
              <div
                className={cn(
                  "flex shrink-0 justify-between gap-3 px-4 py-3 transition-colors",
                  modalHeaderToneClass[headerTone],
                  description ? "items-start" : "items-center",
                  headerClassName,
                )}
              >
                <div
                  className={cn(
                    "flex min-w-0 flex-1 gap-3",
                    description ? "items-start" : "items-center",
                  )}
                >
                  {icon ? (
                    iconVariant === "badge" ? (
                      <div
                        className={cn(
                          "flex size-9 shrink-0 items-center justify-center rounded-control border transition-colors",
                          isDarkHeader
                            ? "bg-white/20 text-white border-white/25 shadow-xs [&>svg]:text-white"
                            : modalIconBadgeToneClass[iconTone],
                          description && "mt-0.5",
                          iconClassName,
                        )}
                        aria-hidden
                      >
                        <span className="inline-flex size-4 items-center justify-center [&>svg]:size-4">
                          {icon}
                        </span>
                      </div>
                    ) : (
                      <span
                        className={cn(
                          "inline-flex size-4 shrink-0 items-center justify-center [&>svg]:size-4",
                          isDarkHeader
                            ? "text-white [&>svg]:text-white"
                            : [
                                "text-primary",
                                iconTone === "danger" && "text-danger-fg",
                                iconTone === "warning" && "text-warning-fg",
                                iconTone === "info" && "text-info-fg",
                                iconTone === "success" && "text-success-fg",
                                iconTone === "neutral" && "text-text-secondary",
                              ],
                          description && "mt-1",
                          iconClassName,
                        )}
                        aria-hidden
                      >
                        {icon}
                      </span>
                    )
                  ) : null}

                  <div className="min-w-0 flex-1">
                    {typeof title === "string" ? (
                      <Dialog.Title
                        className={cn(
                          "m-0 text-base font-bold leading-snug sm:text-lg",
                          isDarkHeader ? "text-white" : "text-text",
                        )}
                      >
                        {title}
                      </Dialog.Title>
                    ) : (
                      <Dialog.Title asChild>
                        <div
                          className={cn(
                            "m-0 text-base font-bold leading-snug sm:text-lg",
                            isDarkHeader ? "text-white" : "text-text",
                          )}
                        >
                          {title}
                        </div>
                      </Dialog.Title>
                    )}
                    {description ? (
                      typeof description === "string" ? (
                        <Dialog.Description
                          className={cn(
                            "mt-0.5 mb-0 text-caption",
                            isDarkHeader ? "text-white/80" : "text-text-secondary",
                          )}
                        >
                          {description}
                        </Dialog.Description>
                      ) : (
                        <Dialog.Description asChild>
                          <div
                            className={cn(
                              "mt-0.5 text-caption",
                              isDarkHeader ? "text-white/80" : "text-text-secondary",
                            )}
                          >
                            {description}
                          </div>
                        </Dialog.Description>
                      )
                    ) : (
                      <Dialog.Description className="sr-only">
                        {typeof title === "string" ? title : "Ngăn kéo"}
                      </Dialog.Description>
                    )}
                  </div>
                </div>
                {dismissible ? (
                  <Dialog.Close asChild>
                    <IconButton
                      label="Đóng"
                      icon={<X className="size-5" />}
                      size="md"
                      tooltip={false}
                      className={cn(
                        "size-9 shrink-0",
                        isDarkHeader
                          ? "text-white/80 hover:text-white hover:bg-white/15 active:bg-white/20"
                          : undefined,
                      )}
                      iconClassName="size-5 [&>svg]:size-5"
                    />
                  </Dialog.Close>
                ) : null}
              </div>
            );
          })()}
          <div
            className={cn(
              "min-h-0 flex-1 overflow-y-auto px-4 py-4 scrollbar-thin overscroll-contain",
              bodyClassName,
            )}
          >
            {children}
          </div>
          {footer ? (
            <div
              className={
                isBottom && fullHeight
                  ? "flex shrink-0 flex-col-reverse gap-2 border-t border-border px-3 py-3"
                  : "flex shrink-0 flex-wrap justify-end gap-2 border-t border-border px-3 py-3"
              }
            >
              {footer}
            </div>
          ) : null}
          <LoadingOverlay visible={loading} label={loadingLabel} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
